import React, { useState, useEffect } from 'react';
import { PaperAirplaneIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GasNomination, GasNominationType, GasNominationStatus, GasContract, GasPod, CreateGasNominationData } from '../../types/gas';
import { gasNominationService, gasContractService, gasPodService } from '../../services/gasService';
import { getGasDay } from '../../utils/gasCalendar';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface GasNominationsSectionProps {
  customerId: string;
}

const GasNominationsSection: React.FC<GasNominationsSectionProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [nominations, setNominations] = useState<GasNomination[]>([]);
  const [contracts, setContracts] = useState<GasContract[]>([]);
  const [gasPods, setGasPods] = useState<GasPod[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingNominationId, setEditingNominationId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CreateGasNominationData>>({
    type: 'day_ahead',
    status: 'draft',
    gasDay: getGasDay().label,
    quantityM3: undefined,
    quantityKwh: undefined,
    notes: ''
  });

  const loadData = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [nominationData, contractData, podData] = await Promise.all([
        gasNominationService.getGasNominationsByCustomerId(customerId),
        gasContractService.getGasContractsByCustomerId(customerId),
        gasPodService.getGasPodsByCustomerId(customerId)
      ]);
      setNominations(nominationData);
      setContracts(contractData);
      setGasPods(podData);
    } catch (error) {
      console.error('Error loading gas nominations:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.nominations') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId]);

  const resetForm = () => {
    setEditingNominationId(null);
    setFormData({
      type: 'day_ahead',
      status: 'draft',
      gasDay: getGasDay().label,
      quantityM3: undefined,
      quantityKwh: undefined,
      notes: ''
    });
  };

  const handleAdd = async () => {
    if (!formData.gasDay) {
      toast.error(t('gas.nomination.errors.gasDayRequired'));
      return;
    }
    if (!formData.type) {
      toast.error(t('gas.nomination.errors.typeRequired'));
      return;
    }

    try {
      const payload = {
        contractId: formData.contractId?.trim() || undefined,
        gasPodId: formData.gasPodId?.trim() || undefined,
        type: formData.type as GasNominationType,
        status: formData.status as GasNominationStatus || 'draft',
        gasDay: formData.gasDay,
        quantityM3: formData.quantityM3 ? Number(formData.quantityM3) : undefined,
        quantityKwh: formData.quantityKwh ? Number(formData.quantityKwh) : undefined,
        notes: formData.notes?.trim() || undefined
      };
      if (editingNominationId) {
        await gasNominationService.updateGasNomination(editingNominationId, payload);
        toast.success(t('gas.messages.saveSuccess', { name: t('gas.sections.nominations') }));
      } else {
        await gasNominationService.createGasNomination({ customerId, ...payload });
        toast.success(t('gas.messages.addSuccess', { name: t('gas.sections.nominations') }));
      }
      setShowAdd(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating gas nomination:', error);
      toast.error(t('gas.messages.addError', { name: t('gas.sections.nominations') }));
    }
  };

  const handleEdit = (nomination: GasNomination) => {
    setFormData({
      contractId: nomination.contractId,
      gasPodId: nomination.gasPodId,
      type: nomination.type,
      status: nomination.status,
      gasDay: nomination.gasDay,
      quantityM3: nomination.quantityM3,
      quantityKwh: nomination.quantityKwh,
      notes: nomination.notes || ''
    });
    setEditingNominationId(nomination.id);
    setShowAdd(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('gas.messages.confirmDelete'))) return;
    try {
      await gasNominationService.deleteGasNomination(id);
      toast.success(t('gas.messages.deleteSuccess', { name: t('gas.sections.nominations') }));
      loadData();
    } catch (error) {
      console.error('Error deleting gas nomination:', error);
      toast.error(t('gas.messages.deleteError', { name: t('gas.sections.nominations') }));
    }
  };

  const handleStatusChange = async (id: string, newStatus: GasNominationStatus) => {
    try {
      await gasNominationService.updateGasNomination(id, { status: newStatus });
      toast.success(t('gas.messages.saveSuccess', { name: t('gas.nomination.status') }));
      loadData();
    } catch (error) {
      console.error('Error updating gas nomination status:', error);
      toast.error(t('gas.messages.saveError', { name: t('gas.nomination.status') }));
    }
  };

  const getTypeLabel = (type: GasNominationType) => {
    switch (type) {
      case 'day_ahead': return 'Előző napi';
      case 'within_day': return 'Napon belüli';
      case 'long_term': return 'Hosszú távú';
      default: return type;
    }
  };

  const getStatusLabel = (status: GasNominationStatus) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'submitted': return 'Beküldött';
      case 'confirmed': return 'Visszaigazolt';
      case 'rejected': return 'Elutasított';
      default: return status;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PaperAirplaneIcon className="h-6 w-6 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('gas.sections.nominations')}</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          {showAdd ? t('gas.common.cancel') : t('gas.common.new')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('gas.common.loading')}</p>
      ) : nominations.length === 0 ? (
        <p className="text-sm text-gray-500">{t('gas.common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {nominations.map(nomination => (
            <div key={nomination.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-gray-900">{nomination.gasDay}</span>
                  <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{getTypeLabel(nomination.type)}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">{getStatusLabel(nomination.status)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  {nomination.quantityM3 !== undefined && <span>{nomination.quantityM3} m³</span>}
                  {nomination.quantityKwh !== undefined && <span className="ml-2">{nomination.quantityKwh} kWh</span>}
                  {(nomination.quantityM3 === undefined && nomination.quantityKwh === undefined) && <span className="italic">{t('gas.nomination.noQuantity')}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {nomination.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(nomination.id, 'submitted')}
                    className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                  >
                    {t('gas.nomination.submit')}
                  </button>
                )}
                {nomination.status === 'submitted' && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(nomination.id, 'confirmed')}
                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    {t('gas.nomination.confirm')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleEdit(nomination)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('gas.common.edit')}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(nomination.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-3">
          <h3 className="text-sm font-semibold text-indigo-900 mb-2">{t('gas.sections.nominations')}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.contract')}</label>
              <select
                value={formData.contractId || ''}
                onChange={e => setFormData({ ...formData, contractId: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{t('gas.nomination.fields.selectContract')}</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractNumber || contract.contractType} ({contract.startDate})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.pod')}</label>
              <select
                value={formData.gasPodId || ''}
                onChange={e => setFormData({ ...formData, gasPodId: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{t('gas.nomination.fields.selectPod')}</option>
                {gasPods.map(pod => (
                  <option key={pod.id} value={pod.id}>{pod.podId}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.type')} *</label>
              <select
                value={formData.type || 'day_ahead'}
                onChange={e => setFormData({ ...formData, type: e.target.value as GasNominationType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="day_ahead">{t('gas.nomination.types.day_ahead')}</option>
                <option value="within_day">{t('gas.nomination.types.within_day')}</option>
                <option value="long_term">{t('gas.nomination.types.long_term')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.gasDay')} *</label>
              <input
                type="date"
                value={formData.gasDay || ''}
                onChange={e => setFormData({ ...formData, gasDay: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.quantityM3')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantityM3 || ''}
                onChange={e => setFormData({ ...formData, quantityM3: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.quantityKwh')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantityKwh || ''}
                onChange={e => setFormData({ ...formData, quantityKwh: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.status')}</label>
            <select
              value={formData.status || 'draft'}
              onChange={e => setFormData({ ...formData, status: e.target.value as GasNominationStatus })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="draft">{t('gas.nomination.statuses.draft')}</option>
              <option value="submitted">{t('gas.nomination.statuses.submitted')}</option>
              <option value="confirmed">{t('gas.nomination.statuses.confirmed')}</option>
              <option value="rejected">{t('gas.nomination.statuses.rejected')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.nomination.fields.notes')}</label>
            <textarea
              value={formData.notes || ''}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              {t('gas.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              {t('gas.common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GasNominationsSection;
